/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import api from './api';
import type { Role, User } from '../types';

interface ApiUser {
  id: string;
  email: string;
  name: string;
  role: Role | 'demo';
  disabled?: boolean;
  company?: string;
  phone?: string;
  emailVerified?: boolean;
  inviteStatus: 'none' | 'pending' | 'expired';
  inviteExpiry?: string;
  createdAt: string;
  updatedAt?: string;
}

interface InviteUserResponse {
  user: ApiUser;
  inviteEmailSent: boolean;
}

export interface UserDemandSourceSummary {
  demandSourceId: string;
  name: string;
  method: string;
  enabled: boolean;
  visibility?: 'public' | 'private';
  createdAt: string;
}

export interface AdminResetPasswordResponse {
  success: boolean;
  message: string;
  emailSent?: boolean;
  resetToken?: string;
  tempPassword?: string;
}

function toUiRole(role: ApiUser['role']): Role {
  return role === 'demo' ? 'demo_user' : role;
}

function toApiRole(role?: Role): Role | 'demo' | undefined {
  if (!role) return undefined;
  return role === 'demo_user' ? 'demo' : role;
}

function normalizeUser(apiUser: ApiUser): User {
  return {
    id: apiUser.id,
    email: apiUser.email,
    name: apiUser.name,
    role: toUiRole(apiUser.role),
    disabled: apiUser.disabled,
    company: apiUser.company,
    phone: apiUser.phone,
    createdAt: apiUser.createdAt,
    updatedAt: apiUser.updatedAt,
    verified: apiUser.emailVerified ?? false,
    emailVerified: apiUser.emailVerified,
    inviteStatus: apiUser.inviteStatus ?? 'none',
    inviteExpiry: apiUser.inviteExpiry,
  };
}

export const userService = {
  async list(): Promise<User[]> {
    const { data } = await api.get<ApiUser[]>('/api/users');
    return data.map(normalizeUser);
  },

  async create(input: {
    email: string;
    name: string;
    password: string;
    role: Role;
    company?: string;
    phone?: string;
  }): Promise<User> {
    const { data } = await api.post<ApiUser>('/api/users', {
      ...input,
      role: toApiRole(input.role),
    });
    return normalizeUser(data);
  },

  async invite(input: {
    email: string;
    role: Role;
    name?: string;
    company?: string;
    phone?: string;
    message?: string;
  }): Promise<{ user: User; inviteEmailSent: boolean }> {
    const { data } = await api.post<InviteUserResponse>('/api/users/invite', {
      ...input,
      role: toApiRole(input.role),
    });
    return {
      user: normalizeUser(data.user),
      inviteEmailSent: data.inviteEmailSent,
    };
  },

  async get(id: string): Promise<User> {
    const { data } = await api.get<ApiUser>(`/api/users/${id}`);
    return normalizeUser(data);
  },

  async update(id: string, updates: Partial<User> & { role?: Role }): Promise<User> {
    const payload = {
      ...updates,
      role: toApiRole(updates.role),
    };
    const { data } = await api.patch<ApiUser>(`/api/users/${id}`, payload);
    return normalizeUser(data);
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/api/users/${id}`);
  },

  async adminResetPassword(userId: string): Promise<AdminResetPasswordResponse> {
    const { data } = await api.post<AdminResetPasswordResponse>(`/api/users/${userId}/reset-password`);
    return data;
  },

  async disable(id: string): Promise<User> {
    const { data } = await api.patch<ApiUser>(`/api/users/${id}/disable`);
    return normalizeUser(data);
  },

  async enable(id: string): Promise<User> {
    const { data } = await api.patch<ApiUser>(`/api/users/${id}/enable`);
    return normalizeUser(data);
  },

  async resendVerification(id: string): Promise<void> {
    await api.post(`/api/users/${id}/resend-verification`);
  },

  async resendInvite(id: string): Promise<void> {
    await api.post(`/api/users/${id}/resend-invite`);
  },

  async getDemandSources(id: string): Promise<UserDemandSourceSummary[]> {
    try {
      const { data } = await api.get<UserDemandSourceSummary[]>(`/api/users/${id}/demand-sources`);
      return data;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },
};
